import "./index.css";

import { DrawerContent } from "@tql/ui-components-react/kendo/layout";
import { Drawer } from "@tql/ui-components-react/tql/layout";
import { useAtom } from "jotai";
import type { ReactNode } from "react";

import { useGetCurrentUserInfo } from "~/shared/hooks/use-current-user-info.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import { useSharedListsAccess } from "~/shared/hooks/use-shared-lists-access.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

import { LeftNavbar } from "./components/left-navbar/left-navbar.tsx";
import { MobileNavbar } from "./components/mobile-navbar/mobile-navbar.tsx";
import type { NavContext } from "./nav-items.tsx";
import { getLowerNavItems, getUpperNavItems } from "./nav-items.tsx";
import { drawerExpandedAtom, feedbackOpen } from "./state/navbar-state.ts";

type NavBarProps = {
	children: ReactNode;
};

export const NavBar = ({ children }: Readonly<NavBarProps>) => {
	const [expanded, setExpanded] = useAtom(drawerExpandedAtom);
	const { handleLogout, username } = useIsAuthenticated();
	const { isInternal } = useDeploymentType();
	const ftlQuoteFlag = useFeatureFlagGate("enableTraxFtlModernized");
	const addressBookFlag = useFeatureFlagGate("traxltlAddressBook");
	const commoditiesFlag = useFeatureFlagGate("traxLtlCommodityList");
	const autoDispatchFlag = useFeatureFlagGate("traxLtlAutoDispatch");
	const autoDispatchAdminFlag = useFeatureFlagGate("traxLtlAutoDispatchAdmin");
	const { isEnabled: isFeedbackEnabled, isReady: isFeedbackReady } =
		useFeatureFlagGate("traxLtlFeedback");
	const templatesPageFlag = useFeatureFlagGate("traxLtlTemplatesPage");
	const notificationsFlag = useFeatureFlagGate("traxLtlNotifications");
	const mySettingsFlag = useFeatureFlagGate("traxLtlMySettings");
	const accountSettingsFlag = useFeatureFlagGate("traxLtlAccountSettings");
	const adminFlag = useFeatureFlagGate("traxLtlAdminPage");
	const reportsFlag = useFeatureFlagGate("traxLtlReportsPage");
	const customerProfileSwitchFlag = useFeatureFlagGate(
		"enableCustomerProfileSwitch",
	);
	const vendorManagementFlag = useFeatureFlagGate("traxltlVendorManagement");
	const routingGuideFlag = useFeatureFlagGate("traxLtlRoutingGuide");

	const [selectedCustomer] = useAtom(selectedCustomerAtom);
	const [, setFeedbackOpen] = useAtom(feedbackOpen);

	const { data: currentUser } = useGetCurrentUserInfo(isInternal);
	const userRole = currentUser?.role ?? "";
	const { isVendor } = useIsVendor();
	const { canManageSharedLists } = useSharedListsAccess();

	const navContext: NavContext = {
		isInternal,
		isVendor,
		canManageSharedLists,
		username,
		userRole,
		customerId: selectedCustomer?.id?.toString() || "",
		flags: {
			ftlQuote: ftlQuoteFlag.isEnabled,
			reports: reportsFlag.isEnabled,
			notifications: notificationsFlag.isEnabled,
			addressBook: addressBookFlag.isEnabled,
			autoDispatch: autoDispatchFlag.isEnabled,
			autoDispatchAdmin: autoDispatchAdminFlag.isEnabled,
			templates: templatesPageFlag.isEnabled,
			commodities: commoditiesFlag.isEnabled,
			mySettings: mySettingsFlag.isEnabled,
			accountSettings: accountSettingsFlag.isEnabled,
			admin: adminFlag.isEnabled,
			customerProfileSwitch: customerProfileSwitchFlag.isEnabled,
			routingGuide: routingGuideFlag.isEnabled,
			vendorManagement: vendorManagementFlag.isEnabled,
			feedbackReady: isFeedbackEnabled && isFeedbackReady,
		},
		onFeedbackClick: () => setFeedbackOpen(true),
		onLogout: handleLogout,
	};

	const leftNavBarUpperItemDetails = getUpperNavItems(navContext);
	const leftNavBarLowerItemDetails = getLowerNavItems(navContext);

	const closeMobileNavbar = () => {
		setExpanded(false);
	};

	return (
		<div className={"navbar-drawer"}>
			<Drawer expanded={expanded} mode={"overlay"} position={"start"}>
				<DrawerContent>
					<div id={"nav-container"}>
						{expanded ? (
							<button
								aria-label={"Close navigation menu"}
								className={"mobile-nav-backdrop"}
								onClick={closeMobileNavbar}
								type={"button"}
							/>
						) : null}
						<LeftNavbar
							forceShow={expanded}
							lowerItemDetails={leftNavBarLowerItemDetails}
							onItemActivated={closeMobileNavbar}
							upperItemDetails={leftNavBarUpperItemDetails}
						/>
						<div id={"right-section"}>
							<div id={"bottom-section"}>
								<MobileNavbar />
								{children}
							</div>
						</div>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
};
